/**
 * T9006 · Stale-while-error default · throw-allowlist predicate.
 *
 * File 05 §L2645 / §L2727 / §L2779 model rejection.
 *
 * The "throw to the boundary on every error" model is REJECTED: it
 * produces full-page failure on a single 500 from a single endpoint
 * while the rest of the app is healthy. Stale-while-error is the
 * read-path default:
 *
 *   - If cached data exists, keep showing it.
 *   - Surface the error as a non-blocking pip / toast / inline banner.
 *   - Only boundaries catch THROW-class errors:
 *       - `schema/SHAPE_MISMATCH` — backend version mismatch; cached
 *          data is no longer trustworthy. Throw.
 *       - `auth/SDK_NOT_INITIALIZED` — programmer error; throw.
 *       - `auth/JWT_INVALID` — non-recoverable session corruption; throw.
 *
 * TanStack integration: pass `throwOnError: shouldThrowToBoundary` to
 * each `useQuery` (or set it on the QueryClient default). The predicate
 * returns `true` only for the allowlist above so every other error
 * settles into `query.error` and stays out of React's error boundary.
 *
 * The predicate already lives in `../types/SdkError.ts`. This module
 * re-exports it under a stage-1-spec name + ships the THROW_ALLOWLIST
 * set as a frozen contract object the devtool can introspect.
 */

import type { AuthCode, SchemaCode, SdkErrorCategory } from '../../types/SdkError.js';
import { shouldThrowToBoundary, THROW_CATEGORIES } from '../../types/SdkError.js';

/**
 * The frozen allowlist of `{ category, code }` tuples that bubble to the
 * `<SdkErrorBoundary>`. Everything else stays inside `query.error` and
 * surfaces as a non-blocking pip.
 *
 * Stage-1 set (matched against the T108 code unions in
 * `../types/SdkError.ts`):
 *   - `schema/SHAPE_MISMATCH` — backend version mismatch; throw.
 *   - `schema/PROTO_DECODE_FAILED` — NATS event decode failure; throw.
 *   - `schema/JSON_PARSE_FAILED` — body could not be parsed; throw.
 *   - `schema/UNKNOWN_ENUM` — enum drift; throw.
 *   - `auth/JWT_INVALID` — broker rejected our JWT; session is gone; throw.
 *
 * `auth/JWT_EXPIRED` is NOT on this list — the refresh ladder handles
 * it transparently. Likewise `auth/UNAUTHORIZED` / `FORBIDDEN` settle
 * into the auth-redirect mutation-UX bucket, NOT a boundary throw.
 */
export const THROW_ALLOWLIST: ReadonlyArray<{
  readonly category: SdkErrorCategory;
  readonly code: string;
}> = Object.freeze([
  { category: 'schema' as const, code: 'SHAPE_MISMATCH' as SchemaCode },
  { category: 'schema' as const, code: 'PROTO_DECODE_FAILED' as SchemaCode },
  { category: 'schema' as const, code: 'JSON_PARSE_FAILED' as SchemaCode },
  { category: 'schema' as const, code: 'UNKNOWN_ENUM' as SchemaCode },
  { category: 'auth' as const, code: 'JWT_INVALID' as AuthCode },
]);

/**
 * The predicate `useQuery({ throwOnError })` calls. Returns `true` only
 * for the THROW_ALLOWLIST entries above; everything else returns false
 * so the error settles into `query.error` (stale-while-error).
 *
 * Non-SdkError throwables ALSO settle into `query.error`. The real
 * population here is browser-thrown `TypeError: Failed to fetch`
 * (network blip, DNS, CORS-preflight failure, dev backend hot reload)
 * and `AbortError` — not programmer mistakes. Crashing the entire
 * market-detail / portfolio / launchpad page on a transient network
 * blip violates the stale-while-error contract documented above
 * ("only schema/invariant errors throw"). Genuine programmer errors
 * (uncaught TypeError from app code) still surface via React's
 * unhandled-rejection handler and the dev error overlay — they don't
 * need an extra escape hatch here.
 */
export function staleWhileErrorThrowOnError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const e = err as { name?: unknown; category?: unknown; code?: unknown };
  if (e.name !== 'SdkError') return false;
  const cat = e.category as SdkErrorCategory | undefined;
  const code = e.code as string | undefined;
  if (cat === undefined || code === undefined) return false;
  for (const entry of THROW_ALLOWLIST) {
    if (entry.category === cat && entry.code === code) return true;
  }
  return false;
}

/**
 * Back-compat re-export — `shouldThrowToBoundary(err)` is the
 * pre-existing type-narrowing variant (takes a typed `SdkError`).
 * Prefer `staleWhileErrorThrowOnError(unknown)` in `throwOnError`
 * callbacks because TanStack passes `unknown` to the callback.
 */
export { shouldThrowToBoundary, THROW_CATEGORIES };
